import { OngoingReportingType } from '../interfaces/bbc'
import {
  QboDataset,
  QboPullPreset,
  QboTransactionSide,
  QboTransactionStatus,
} from '../interfaces/qboReporting'

export const QBO_DATASET_LABELS: Record<QboDataset, string> = {
  [QboDataset.BalanceSheet]: 'Balance sheet',
  [QboDataset.IncomeStatement]: 'Income statement',
  [QboDataset.ArAging]: 'AR aging',
  [QboDataset.ApAging]: 'AP aging',
  [QboDataset.GeneralLedger]: 'General ledger',
  [QboDataset.TrialBalance]: 'Trial balance',
  [QboDataset.TopCustomers]: 'Top customers',
  [QboDataset.TopVendors]: 'Top vendors',
}

// What each report lets a caller tune, and which selections bring it into play.
//
// Reports differ in what "more history" even means — a statement adds period
// columns, an aging adds whole month-end snapshots, a ledger widens a date range
// — so each setting names its own unit rather than sharing one depth control.
export type QboReportSettingKey =
  'periods' | 'arAgingMonths' | 'apAgingMonths' | 'ledgerMonths' | 'topCounterpartyLimit'

export interface IQboReportSetting {
  key: QboReportSettingKey
  label: string
  help: string
  min: number
  max: number
  defaultValue: number
  datasets: QboDataset[]
}

export const QBO_REPORT_SETTINGS: IQboReportSetting[] = [
  {
    key: 'periods',
    label: 'Months of history',
    help: 'One column per month, ending with the as-of month.',
    min: 1,
    max: 120,
    defaultValue: 12,
    datasets: [
      QboDataset.BalanceSheet,
      QboDataset.IncomeStatement,
      QboDataset.TopCustomers,
      QboDataset.TopVendors,
    ],
  },
  {
    key: 'arAgingMonths',
    label: 'AR month-end snapshots',
    help: 'A separate aging as at each month end, oldest first. 1 is current only.',
    min: 1,
    max: 24,
    defaultValue: 1,
    datasets: [QboDataset.ArAging],
  },
  {
    key: 'apAgingMonths',
    label: 'AP month-end snapshots',
    help: 'A separate aging as at each month end, oldest first. 1 is current only.',
    min: 1,
    max: 24,
    defaultValue: 1,
    datasets: [QboDataset.ApAging],
  },
  {
    key: 'ledgerMonths',
    label: 'Ledger months',
    help: 'How far back the transaction list and trial balance reach.',
    min: 1,
    max: 120,
    defaultValue: 12,
    datasets: [QboDataset.GeneralLedger, QboDataset.TrialBalance],
  },
  {
    key: 'topCounterpartyLimit',
    label: 'Top counterparties',
    help: 'How many customers or vendors to rank.',
    min: 1,
    max: 500,
    defaultValue: 50,
    datasets: [QboDataset.TopCustomers, QboDataset.TopVendors],
  },
]

// The settings a given report selection actually exposes.
export const settingsForDatasets = (datasets: QboDataset[]): IQboReportSetting[] =>
  QBO_REPORT_SETTINGS.filter((setting) =>
    setting.datasets.some((dataset) => datasets.includes(dataset)),
  )

export interface IPresetDefaults {
  datasets: QboDataset[]
  periods: number
  // How many month-end snapshots of aging to take. 1 means "current only".
  arAgingMonths: number
  apAgingMonths: number
}

// History depths carried over from what each stage of the lifecycle has always
// pulled, so a direct pull produces the same shaped deck as the Codat one.
export const QBO_PULL_PRESETS: Record<QboPullPreset, IPresetDefaults> = {
  // Nightly refresh for a Current client: a rolling year of statements.
  [QboPullPreset.Portfolio]: {
    datasets: [QboDataset.BalanceSheet, QboDataset.IncomeStatement],
    periods: 12,
    arAgingMonths: 1,
    apAgingMonths: 1,
  },
  // Prospect screening: five years of statements plus where the book stands today.
  [QboPullPreset.BusinessDevelopment]: {
    datasets: [
      QboDataset.BalanceSheet,
      QboDataset.IncomeStatement,
      QboDataset.ArAging,
      QboDataset.ApAging,
    ],
    periods: 60,
    arAgingMonths: 1,
    apAgingMonths: 1,
  },
  // Underwriting: everything, with aging replayed month by month so the trend is
  // visible rather than a single snapshot.
  [QboPullPreset.Underwriting]: {
    datasets: [
      QboDataset.BalanceSheet,
      QboDataset.IncomeStatement,
      QboDataset.ArAging,
      QboDataset.ApAging,
      QboDataset.GeneralLedger,
      QboDataset.TrialBalance,
      QboDataset.TopCustomers,
      QboDataset.TopVendors,
    ],
    periods: 120,
    arAgingMonths: 12,
    apAgingMonths: 6,
  },
}

// A refresh of the Invoices or Bills tab inside this window answers from the
// stored copy instead of calling QuickBooks again.
export const QBO_TRANSACTIONS_REFRESH_COOLDOWN_MINUTES = 15

export const QBO_TRANSACTION_STATUS_LABELS: Record<QboTransactionStatus, string> = {
  [QboTransactionStatus.Open]: 'Open',
  [QboTransactionStatus.PartiallyPaid]: 'Partially paid',
  [QboTransactionStatus.Paid]: 'Paid',
  [QboTransactionStatus.Voided]: 'Voided',
  [QboTransactionStatus.Unapplied]: 'Unapplied credit',
  [QboTransactionStatus.Applied]: 'Applied credit',
}

// The QuickBooks entities synced for each side. The first is the document itself;
// the second is its credit, which is stored with a negative total.
export const QBO_TRANSACTION_ENTITIES: Record<QboTransactionSide, readonly string[]> = {
  [OngoingReportingType.AR]: ['Invoice', 'CreditMemo'],
  [OngoingReportingType.AP]: ['Bill', 'VendorCredit'],
}

export const QBO_CREDIT_ENTITIES: readonly string[] = Object.values(QBO_TRANSACTION_ENTITIES).map(
  ([, credit]) => credit,
)

// Every vendor-side entity a transaction id can belong to. A superset of the
// synced AP entities: documents are also fetched for bill payments and expenses.
export const QBO_VENDOR_ENTITIES: readonly string[] = [
  ...QBO_TRANSACTION_ENTITIES[OngoingReportingType.AP],
  'BillPayment',
  'Purchase',
  'Check',
]

export const sideOfEntity = (entity: string): QboTransactionSide =>
  QBO_VENDOR_ENTITIES.includes(entity) ? OngoingReportingType.AP : OngoingReportingType.AR

export const QBO_TRANSACTION_TYPE_LABELS: Record<string, string> = {
  Invoice: 'Invoice',
  CreditMemo: 'Credit memo',
  Bill: 'Bill',
  VendorCredit: 'Vendor credit',
}

// Aging buckets for anything still owed, by days past due. Current covers
// not-yet-due as well as due today.
export const QBO_AGING_BUCKETS = ['Current', '1 - 30', '31 - 60', '61 - 90', '91+'] as const
